// Attribution capture: persists UTM/referrer signals from first visit
// so they can be attached to a future signup.

const STORAGE_KEY = "gr_attribution_v1";
const FIRST_TOUCH_KEY = "gr_first_touch_v1";

export interface Attribution {
  source?: string;
  first_touch?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  hook?: string;
}

function detectPlatformFromReferrer(ref: string): string | undefined {
  if (!ref) return undefined;
  const r = ref.toLowerCase();
  if (r.includes("tiktok")) return "tiktok";
  if (r.includes("youtube") || r.includes("youtu.be")) return "youtube";
  if (r.includes("instagram")) return "instagram";
  if (r.includes("reddit")) return "reddit";
  if (r.includes("twitter") || r.includes("x.com") || r.includes("t.co")) return "x";
  if (r.includes("facebook") || r.includes("fb.com")) return "facebook";
  if (r.includes("linkedin")) return "linkedin";
  if (r.includes("google")) return "google";
  return undefined;
}

/**
 * Marks this device as quiz-referred and records quiz UTMs immediately.
 * Called at the point of an in-app (client-side) navigation to the analyzer,
 * where a full page load — and therefore captureAttributionFromUrl() — never runs.
 * Safe for both fresh visitors and returning ones with an existing attribution blob.
 */
export function markQuizReferral(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("gr_quiz_referred", "1");

    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, "quiz");
    }

    // Latest-touch always reflects the quiz hand-off, even if a blob already exists.
    const existingRaw = sessionStorage.getItem(STORAGE_KEY);
    const existing: Attribution = existingRaw ? JSON.parse(existingRaw) : {};
    const merged: Attribution = {
      ...existing,
      source: "quiz",
      campaign: "spot_the_pattern",
      first_touch: localStorage.getItem(FIRST_TOUCH_KEY) || "quiz",
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch (e) {
    console.warn("[attribution] quiz referral flag failed", e);
  }
}

export function captureAttributionFromUrl(): void {

  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer || "";
    // Short bio links (/tt, /yt, /ig, /pin) set their own source on a full page load.
    if (applyShortLinkAttribution(window.location.pathname, window.location.search)) return;

    const current: Attribution = {
      source:
        params.get("utm_source") ||
        params.get("source") ||
        detectPlatformFromReferrer(referrer) ||
        undefined,
      medium: params.get("utm_medium") || params.get("medium") || undefined,
      campaign: params.get("utm_campaign") || params.get("campaign") || undefined,
      content: params.get("utm_content") || params.get("content") || undefined,
      hook: params.get("hook") || params.get("utm_term") || undefined,
    };

    // Durable quiz-referral flag: survives across sessions/tabs/OAuth redirects.
    // Set on ANY visit where utm_source=quiz appears, so it works regardless of
    // whether signup happens via email/password or Google OAuth (which drops
    // all signup metadata).
    if (current.source === "quiz") {
      localStorage.setItem("gr_quiz_referred", "1");
    }

    // First-touch is sticky forever
    if (!localStorage.getItem(FIRST_TOUCH_KEY) && current.source) {
      localStorage.setItem(FIRST_TOUCH_KEY, current.source);
    }
    current.first_touch =
      localStorage.getItem(FIRST_TOUCH_KEY) || current.source || undefined;

    // Latest-touch is updated whenever new attribution arrives
    const hasAny = Object.values(current).some(Boolean);
    if (hasAny) {
      const existingRaw = sessionStorage.getItem(STORAGE_KEY);
      const existing: Attribution = existingRaw ? JSON.parse(existingRaw) : {};
      const merged: Attribution = { ...existing, ...current };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      console.log("[attribution] captured", merged);
    }
  } catch (e) {
    console.warn("[attribution] capture failed", e);
  }
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const data: Attribution = raw ? JSON.parse(raw) : {};
    if (!data.first_touch) {
      const ft = localStorage.getItem(FIRST_TOUCH_KEY);
      if (ft) data.first_touch = ft;
    }
    return data;
  } catch {
    return {};
  }
}

export const SHORT_LINK_SOURCES: Record<string, string> = {
  "/tt": "tiktok",
  "/yt": "youtube",
  "/ig": "instagram",
  "/pin": "pinterest",
};

/**
 * Records attribution for a short bio link. Returns true when the path is a short link.
 * Explicit utm_source in the query string wins over the platform default.
 */
export function applyShortLinkAttribution(pathname: string, search: string): boolean {
  if (typeof window === "undefined") return false;
  const platform = SHORT_LINK_SOURCES[pathname.replace(/\/+$/, "") || "/"];
  if (!platform) return false;
  try {
    const params = new URLSearchParams(search);
    const hasUtmSource = !!params.get("utm_source");
    const current: Attribution = {
      source: params.get("utm_source") || platform,
      medium: params.get("utm_medium") || params.get("medium") || undefined,
      campaign: params.get("utm_campaign") || (hasUtmSource ? undefined : "bio"),
      content: params.get("utm_content") || params.get("content") || undefined,
      hook: params.get("hook") || params.get("utm_term") || undefined,
    };
    if (!localStorage.getItem(FIRST_TOUCH_KEY) && current.source) {
      localStorage.setItem(FIRST_TOUCH_KEY, current.source);
    }
    current.first_touch = localStorage.getItem(FIRST_TOUCH_KEY) || current.source;
    const existingRaw = sessionStorage.getItem(STORAGE_KEY);
    const existing: Attribution = existingRaw ? JSON.parse(existingRaw) : {};
    const clean = Object.fromEntries(Object.entries(current).filter(([, v]) => v));
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...clean }));
  } catch (e) {
    console.warn("[attribution] short link capture failed", e);
  }
  return true;
}

/** Attribution flattened into event metadata keys. */
export function attributionMetadata(): Record<string, string> {
  const a = getAttribution();
  const out: Record<string, string> = {};
  if (a.source) out.attr_source = a.source;
  if (a.medium) out.attr_medium = a.medium;
  if (a.campaign) out.attr_campaign = a.campaign;
  if (a.content) out.attr_content = a.content;
  if (a.hook) out.attr_hook = a.hook;
  if (a.first_touch) out.first_touch = a.first_touch;
  return out;
}
